import {
  Grid,
  Group,
  Loader,
  MultiSelect,
  Stack,
  TextInput,
  Textarea,
  Text,
  Anchor,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import {
  addProfileToResource,
  createReference,
  formatHumanName,
} from '@medplum/core';
import { Coding, Condition, Encounter, Patient } from '@medplum/fhirtypes';
import { Form, SubmitButton } from '@medplum/react';
import { FC, Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ARKON, HL7, SYSTEMS } from '@consts/systems.consts';
import { useOmimDisease } from '@hooks/useOmimDisease';
import { IconCopy, IconEdit, IconLink, IconSearch } from '@tabler/icons-react';
import { HpoItem } from '@/types/hpo.types';
import { usePatientHpo } from '@/hooks/usePatientHpo';

//------------------------------------------------------------------------------
interface RenderHpoListProps {
  hpoTerms: HpoItem[];
  hpoData: HpoItem[];
}

const RenderHpoList: FC<RenderHpoListProps> = ({ hpoTerms, hpoData }) => {
  const diseaseHpoIds = new Set(hpoTerms.map((hpo) => hpo.id));
  const additionalPatientHpos = hpoData.filter(
    (hpo) => !diseaseHpoIds.has(hpo.id)
  );

  const { t } = useTranslation(['patient-pages']);

  return (
    <>
      {(hpoTerms.length > 0 || hpoData.length > 0) && (
        <>
          {/* HPOs from Disease */}
          {hpoTerms.length > 0 && (
            <>
              <Tooltip
                label={t(
                  'summaryPage.summary.geneticDiseases.dialog.tooltips.hpo'
                )}
                position="top-start"
                color="gray"
                withArrow
              >
                <Text fw={500}>
                  {t(
                    'summaryPage.summary.geneticDiseases.dialog.hpoFromDiagnosis'
                  )}
                </Text>
              </Tooltip>
              <Grid>
                {hpoTerms.map((hpo) => {
                  const isPatientHasIt = hpoData.some((p) => p.id === hpo.id);
                  return (
                    <Fragment key={hpo.id}>
                      <Grid.Col span={3}>
                        <Text size="sm" c={isPatientHasIt ? 'green' : 'dimmed'}>
                          {hpo.id}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={9}>
                        <Text
                          size="sm"
                          c={isPatientHasIt ? 'green' : undefined}
                        >
                          {hpo.name}
                        </Text>
                      </Grid.Col>
                    </Fragment>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Patient-only HPOs */}
          {additionalPatientHpos.length > 0 && (
            <>
              <Text fw={500}>
                {t('summaryPage.summary.geneticDiseases.dialog.hpoOnlyPatient')}
              </Text>
              <Grid>
                {additionalPatientHpos.map((hpo) => (
                  <Fragment key={hpo.id}>
                    <Grid.Col span={3}>
                      <Text size="sm" c="purple">
                        {hpo.id}
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={9}>
                      <Text size="sm" c="purple">
                        {hpo.name}
                      </Text>
                    </Grid.Col>
                  </Fragment>
                ))}
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
};

// -----------------------------------------------------------------------------
export interface GeneticDiseaseDialogProps {
  readonly patient: Patient;
  readonly encounter?: Encounter;
  readonly condition?: Condition;
  readonly onSubmit: (condition: Condition) => void;
}

// -----------------------------------------------------------------------------
export const GeneticDiseaseDialog: FC<GeneticDiseaseDialogProps> = ({
  patient,
  encounter,
  condition,
  onSubmit,
}) => {
  const { t } = useTranslation(['patient-pages']);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    selectedOmim,
    isLoading,
    handleSearchChange,
    handleOmimSelection,
    orphaCode,
    isFetchingOrpha,
    icdCode,
    isFetchingIcd,
    hpoTerms,
  } = useOmimDisease(condition);

  const { hpoData } = usePatientHpo();
  const [isEditingGeneReview, setIsEditingGeneReview] = useState(false);
  const existingGeneReviewLink =
    condition?.extension?.find((ext) => ext.url === ARKON.SD.GENE_REVIEW)
      ?.valueUrl ?? '';

  const [onsetDate, setOnsetDate] = useState<Date | null>(
    condition?.onsetDateTime ? new Date(condition.onsetDateTime) : null
  );

  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(
    (formData: Record<string, string>) => {
      const codings: Coding[] = [];

      if (selectedOmim?.id) {
        codings.push({
          system: SYSTEMS.OMIM,
          code: selectedOmim.id.replace(/^OMIM:/, ''),
          display: selectedOmim.name,
        });
      }

      if (formData.orphaCode) {
        codings.push({
          system: SYSTEMS.ORPHA,
          code: formData.orphaCode,
        });
      }

      if (formData.icdCode) {
        codings.push({
          system: ARKON.CS.ICD10.BASE,
          code: formData.icdCode,
        });
      }

      const extensions = [...(condition?.extension ?? [])].filter(
        (ext) => ext.url !== ARKON.SD.GENE_REVIEW
      );

      if (formData.geneReviewLink?.trim()) {
        extensions.push({
          url: ARKON.SD.GENE_REVIEW,
          valueUrl: formData.geneReviewLink.trim(),
        });
      }

      const updatedCondition: Condition = addProfileToResource(
        {
          ...condition,
          resourceType: 'Condition',
          category: [
            {
              coding: [
                {
                  system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
                  code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES
                    .GENETIC_DISEASE,
                  display: 'Genetic Disease',
                },
              ],
              text: 'Genetic Disease',
            },
          ],
          subject: {
            reference: `Patient/${patient.id}`,
            display: patient ? formatHumanName(patient.name?.[0]) : '',
          },
          encounter: encounter && createReference(encounter),
          code: {
            coding: codings,
            text: selectedOmim?.name,
          },
          extension: extensions,
          onsetDateTime: onsetDate
            ? onsetDate.toISOString().split('T')[0]
            : undefined,
          note: [
            {
              text:
                formData.notes.trim() !== ''
                  ? formData.notes
                  : selectedOmim?.description || 'No description available',
            },
          ],
        },
        HL7.ORG.SD.CONDITION_PROBLEM
      );

      onSubmit(updatedCondition);
    },
    [patient, encounter, condition, selectedOmim, onsetDate, onSubmit]
  );

  // ---------------------------------------------------------------------------
  return (
    <Form key={condition?.id} onSubmit={handleSubmit}>
      <Stack>
        <MultiSelect
          label={t('summaryPage.summary.geneticDiseases.title')}
          data={searchResults.map((result) => result.name)}
          value={selectedOmim ? [selectedOmim?.name] : []}
          onChange={(coding) => {
            handleOmimSelection(coding[0]);
          }}
          onSearchChange={handleSearchChange}
          searchValue={searchQuery}
          maxValues={1}
          searchable={!selectedOmim}
          onBlur={() => setSearchQuery('')}
          placeholder={
            selectedOmim
              ? ''
              : t(
                  'summaryPage.summary.geneticDiseases.dialog.searchPlaceholder'
                )
          }
          rightSection={
            <div style={{ pointerEvents: 'auto' }}>
              {selectedOmim ? (
                <Tooltip
                  label={t(
                    'summaryPage.summary.geneticDiseases.dialog.tooltips.copyDiseaseName'
                  )}
                  withArrow
                  color="gray"
                >
                  <ActionIcon
                    variant="subtle"
                    onClick={() =>
                      navigator.clipboard.writeText(selectedOmim.name)
                    }
                  >
                    <IconCopy size={18} />
                  </ActionIcon>
                </Tooltip>
              ) : isLoading ? (
                <Loader size="xs" />
              ) : (
                <Tooltip
                  label={t(
                    'summaryPage.summary.geneticDiseases.dialog.tooltips.searchOmim'
                  )}
                  withArrow
                  color="gray"
                >
                  <IconSearch size={18} />
                </Tooltip>
              )}
            </div>
          }
        />
        <Grid align="center">
          <Grid.Col span={4}>
            <TextInput
              name="omimCode"
              label={t('summaryPage.summary.geneticDiseases.dialog.omimCode')}
              value={selectedOmim?.id.replace(/^OMIM:/, '') || ''}
              readOnly
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <Tooltip
              label={t(
                'summaryPage.summary.geneticDiseases.dialog.tooltips.orphaTooltip'
              )}
              withArrow
              arrowPosition="side"
              multiline
              color="gray"
              w={190}
              offset={22}
            >
              <TextInput
                name="orphaCode"
                label={t(
                  'summaryPage.summary.geneticDiseases.dialog.orphaCode'
                )}
                defaultValue={orphaCode ?? ''}
                rightSection={isFetchingOrpha ? <Loader size="xs" /> : null}
              />
            </Tooltip>
          </Grid.Col>
          <Grid.Col span={4}>
            <Tooltip
              label={t(
                'summaryPage.summary.geneticDiseases.dialog.tooltips.icd10Tooltip'
              )}
              withArrow
              arrowPosition="side"
              multiline
              color="gray"
              w={190}
              offset={22}
            >
              <TextInput
                name="icdCode"
                label={t(
                  'summaryPage.summary.geneticDiseases.dialog.icd10Code'
                )}
                defaultValue={icdCode ?? ''}
                rightSection={isFetchingIcd ? <Loader size="xs" /> : null}
              />
            </Tooltip>
          </Grid.Col>
        </Grid>
        <Stack>
          <Group align="center" mb={-14}>
            <Text fw={500}>
              {t('summaryPage.summary.geneticDiseases.dialog.geneReviewLabel')}
            </Text>
            {selectedOmim?.name && (
              <Tooltip
                label={t(
                  'summaryPage.summary.geneticDiseases.dialog.tooltips.searchGeneReviews'
                )}
                withArrow
                color="gray"
              >
                <ActionIcon
                  component="a"
                  href={`https://www.ncbi.nlm.nih.gov/books/NBK1116/?term=${encodeURIComponent(
                    selectedOmim.name
                  )}`}
                  target="_blank"
                  variant="subtle"
                >
                  <IconLink size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>

          {isEditingGeneReview || !existingGeneReviewLink ? (
            <TextInput
              name="geneReviewLink"
              type="url"
              defaultValue={existingGeneReviewLink}
              placeholder="https://www.ncbi.nlm.nih.gov/books/NBK1116..."
            />
          ) : (
            <Group align="center" wrap="nowrap" justify="space-between">
              <Anchor href={existingGeneReviewLink} target="_blank">
                {t(
                  'summaryPage.summary.geneticDiseases.dialog.geneReviewLinkText'
                )}
              </Anchor>
              <Tooltip
                label={t(
                  'summaryPage.summary.geneticDiseases.dialog.tooltips.editGeneReviewLink'
                )}
                withArrow
                color="gray"
              >
                <ActionIcon variant="transparent" color="gray">
                  <IconEdit
                    style={{ cursor: 'pointer' }}
                    onClick={() => setIsEditingGeneReview(true)}
                    size={20}
                  />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Stack>
        <RenderHpoList hpoTerms={hpoTerms} hpoData={hpoData} />{' '}
        <DateInputWrapper
          name="onsetDateTime"
          label={t('summaryPage.summary.geneticDiseases.dialog.diagnosisYear')}
          value={onsetDate}
          onChange={setOnsetDate}
        />
        <Textarea
          label={t('summaryPage.summary.geneticDiseases.dialog.notes')}
          name="notes"
          defaultValue={condition?.note?.[0].text}
        />
        <Group justify="flex-end" gap={4} mt="md">
          <SubmitButton>
            {t('summaryPage.summary.geneticDiseases.dialog.save')}
          </SubmitButton>
        </Group>
      </Stack>
    </Form>
  );
};
